import { Stack } from 'component-library';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useMethodologyPageLink } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useMethodologyPageLink';
import { isNullish } from '@/utils/booleans';

import {
  Explainer,
  ExplainerBody,
  ExplainerContent,
  ExplainerCopy,
  ExplainerTitle,
  ExplainerTrigger,
  MethodologyLink,
} from './Explainer';

export type EcologicalIntegrityExplainerProps = {
  trigger?: React.ReactNode;
};

export const EcologicalIntegrityExplainerBU = () => {
  const { t } = useTranslation();
  const methodologyPageLink = useMethodologyPageLink();

  const indicatorSpecies = useFact('r1_conservation_indicator_species_score');
  const speciesOccupancy = useFact('r1_conservation_species_occupancy_score');
  const hasIndicatorSpeciesData = !isNullish(speciesOccupancy?.value) || !isNullish(indicatorSpecies?.value);
  const hasEcologicalIntegrityData = !isNullish(useFact('r1_conservation_ecological_integrity')?.value);
  const hasHabitatIntactnessData = !isNullish(useFact('r1_conservation_threats_i')?.value);

  return (
    <Explainer>
      <ExplainerTrigger data-testid='ecological-integrity-explainer'>{t('global.howDoWeMeasureThis')}</ExplainerTrigger>
      <ExplainerContent>
        <ExplainerTitle>{t('shared.ncaDetail.ecologicalIntegrity.explainers.howDoWeMeasure.title')}</ExplainerTitle>
        <Stack spacing={8} className='items-start pb-6'>
          <ExplainerBody className='pb-0'>
            {hasEcologicalIntegrityData && (
              <ExplainerCopy
                title={t(
                  'shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.howDoWeMeasure.ecologicalIntegrity.title',
                )}
                body={
                  <span className='whitespace-pre-wrap'>
                    {t(
                      'shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.howDoWeMeasure.ecologicalIntegrity.body',
                    )}
                  </span>
                }
              />
            )}
            {hasHabitatIntactnessData && (
              <ExplainerCopy
                title={t(
                  'shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.howDoWeMeasure.habitatIntactness.title',
                )}
                body={
                  <span className='whitespace-pre-wrap'>
                    {t(
                      'shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.howDoWeMeasure.habitatIntactness.body',
                    )}
                  </span>
                }
              />
            )}
            {hasIndicatorSpeciesData && (
              <ExplainerCopy
                title={t(
                  'shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.howDoWeMeasure.indicatorSpecies.title',
                )}
                body={
                  <span className='whitespace-pre-wrap'>
                    {t(
                      'shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.howDoWeMeasure.indicatorSpecies.body',
                    )}
                  </span>
                }
              />
            )}
            {hasEcologicalIntegrityData && (
              <ExplainerCopy
                title={t(
                  'shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.howDoWeMeasure.metricCombination.title',
                )}
                body={
                  <span className='whitespace-pre-wrap'>
                    {t(
                      'shared.ncaDetail.ecologicalIntegrity.historicalDataBU.explainers.howDoWeMeasure.metricCombination.body',
                    )}
                  </span>
                }
              />
            )}
          </ExplainerBody>
          <MethodologyLink to={methodologyPageLink} />
        </Stack>
      </ExplainerContent>
    </Explainer>
  );
};

export const EcologicalIntegrityExplainerVNU = (props: EcologicalIntegrityExplainerProps) => {
  const { t } = useTranslation();
  const methodologyPageLink = useMethodologyPageLink();

  return (
    <Explainer>
      {props.trigger ?? (
        <ExplainerTrigger data-testid='ecological-integrity-explainer'>
          {t('global.howDoWeMeasureThis')}
        </ExplainerTrigger>
      )}
      <ExplainerContent>
        <ExplainerTitle>{t('shared.ncaDetail.ecologicalIntegrity.explainers.howDoWeMeasure.title')}</ExplainerTitle>
        <Stack spacing={8} className='items-start pb-6'>
          <ExplainerBody className='pb-0'>
            <ExplainerCopy
              body={
                <div className='columns-1 leading-[22px] text-text-secondary md:columns-2'>
                  <Trans
                    i18nKey='shared.ncaDetail.ecologicalIntegrity.explainers.howDoWeMeasure.body'
                    components={{
                      ol: <ol className='ml-6 list-decimal space-y-2.5' />,
                      li: <li />,
                    }}
                  />
                </div>
              }
            />
          </ExplainerBody>
          <MethodologyLink to={methodologyPageLink} />
        </Stack>
      </ExplainerContent>
    </Explainer>
  );
};
